/** Marks off-site anchors so they open in a new tab with a safe rel and an announced warning. */
(function () {
  'use strict';

  if (window.DieuxExternalLinks) return;

  const DESCRIPTION_ID = 'a11y-new-window-message';
  const IGNORE_SELECTOR = '[data-external-links="off"]';
  const loader = document.currentScript;
  const internalHosts = new Set();

  /**
   * Comparable form of a host: lower-cased, with a leading `www.` removed.
   *
   * @param {string} host - Host, optionally with a port.
   * @returns {string} Normalized host.
   */
  function normalizeHost(host) {
    const value = String(host || '')
      .trim()
      .toLowerCase();
    return value.startsWith('www.') ? value.slice(4) : value;
  }

  /**
   * Records a host as one this store owns.
   *
   * @param {string} host - Host, optionally with a port.
   * @returns {void}
   */
  function addHost(host) {
    const value = normalizeHost(host);
    if (value) internalHosts.add(value);
  }

  /**
   * Records the host of an origin, accepting a bare host as well as a full URL.
   *
   * @param {string} origin - Origin or host.
   * @returns {void}
   */
  function addOrigin(origin) {
    const value = String(origin || '').trim();
    if (!value) return;
    try {
      addHost(new URL(value).host);
    } catch {
      addHost(value);
    }
  }

  addHost(window.location.host);
  addHost(window.Shopify?.shop);
  String(loader?.dataset.storeOrigins || '')
    .split(',')
    .forEach(addOrigin);

  /**
   * Whether an anchor points at a host this store does not own.
   *
   * @param {HTMLAnchorElement} link - Anchor to classify.
   * @returns {boolean} `true` when the destination is off-site.
   */
  function isExternal(link) {
    const href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return false;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return false;
    }

    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return !internalHosts.has(normalizeHost(url.host));
  }

  /**
   * Applies the new-tab contract to one anchor.
   *
   * @param {Element} node - Candidate anchor.
   * @returns {void}
   */
  function mark(node) {
    if (!(node instanceof HTMLAnchorElement)) return;
    if (node.closest(IGNORE_SELECTOR)) return;

    if (!node.hasAttribute('target') && !node.hasAttribute('download') && isExternal(node)) {
      node.setAttribute('target', '_blank');
    }

    if (node.getAttribute('target') !== '_blank') return;

    const rel = (node.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
    if (!rel.includes('noopener')) {
      rel.push('noopener');
      node.setAttribute('rel', rel.join(' '));
    }

    if (node.hasAttribute('aria-describedby')) return;
    if (!document.getElementById(DESCRIPTION_ID)) return;
    node.setAttribute('aria-describedby', DESCRIPTION_ID);
  }

  /**
   * Marks every anchor inside a root.
   *
   * @param {Element|Document} [root] - Subtree to scan. Defaults to the document.
   * @returns {void}
   */
  function scan(root) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    if (scope instanceof Element) mark(scope);
    scope.querySelectorAll('a[href]').forEach(mark);
  }

  const queue = new Set();
  let scheduled = false;

  function flush() {
    scheduled = false;
    const nodes = [...queue];
    queue.clear();
    nodes.forEach((node) => {
      if (node.isConnected) scan(node);
    });
  }

  /**
   * Batches one subtree into the next microtask so a burst of records costs one pass.
   *
   * @param {Node} node - Node reported by the observer.
   * @returns {void}
   */
  function enqueue(node) {
    if (!(node instanceof Element)) return;
    queue.add(node);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(flush);
  }

  /**
   * Adopts anchors that arrive, or lose their attributes, after the first scan.
   *
   * @param {MutationRecord[]} records - Batch from the document observer.
   * @returns {void}
   */
  function adopt(records) {
    records.forEach((record) => {
      if (record.type === 'attributes') {
        enqueue(record.target);
        return;
      }
      record.addedNodes.forEach(enqueue);
    });
  }

  function start() {
    scan(document);

    new MutationObserver(adopt).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'target'],
    });

    document.addEventListener('shopify:section:load', (event) => {
      scan(event.target instanceof Element ? event.target : document);
    });
  }

  window.DieuxExternalLinks = Object.freeze({ scan, mark, isExternal });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
